import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, ShieldAlert, Plus } from "lucide-react";
import { SiretInput } from "@/components/SiretInput";
import { validateSiret } from "@/lib/siret";

const EMPTY = { company_name: "", contact_prenom: "", contact_nom: "", email: "", telephone: "", siret: "", secteur: "", ville: "", notes: "" };
const L = "text-[10px] uppercase tracking-[0.2em] font-semibold";

/** Formulaire de nouveau lead avec pré-contrôle anti-doublon (bloquant / avertissement). */
export const LeadDialog = ({ open, onOpenChange, secteurs = [], onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [dup, setDup] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => { if (open) { setForm(EMPTY); setDup(null); } }, [open]);

  const payload = (ack) => ({ ...form, acknowledge_warnings: ack });

  const submit = async (ack = false) => {
    if (form.siret.trim() && validateSiret(form.siret).state !== "valid") { toast.error(validateSiret(form.siret).message); return; }
    setBusy(true);
    try {
      const check = await api.post("/bp/leads/check", payload(ack));
      setDup(check.data);
      if (check.data.blocking.length) { toast.error("Prospect déjà suivi — enregistrement impossible"); return; }
      if (check.data.warnings.length && !ack) { toast.warning("Vérifiez les avertissements puis confirmez"); return; }
      const { data } = await api.post("/bp/leads", payload(ack));
      toast.success(`Lead ${data.reference} enregistré`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.code) { setDup({ blocking: d.blocking || [], warnings: d.warnings || [] }); toast.error(d.code === "duplicate" ? "Prospect déjà suivi" : "Avertissement à confirmer"); }
      else toast.error(formatApiError(d));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-black flex items-center gap-2"><Plus className="h-5 w-5 text-[#002FA7]" />Nouveau lead</DialogTitle>
          <DialogDescription className="text-xs">Un prospect déjà client PAG ou déjà suivi par un autre partenaire ne peut pas être enregistré.</DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2 space-y-1.5"><Label className={L}>Société / établissement *</Label><Input value={form.company_name} onChange={(e) => set("company_name")(e.target.value)} data-testid="lead-company" /></div>
          <div className="space-y-1.5"><Label className={L}>Prénom du contact</Label><Input value={form.contact_prenom} onChange={(e) => set("contact_prenom")(e.target.value)} data-testid="lead-contact-prenom" /></div>
          <div className="space-y-1.5"><Label className={L}>Nom du contact *</Label><Input value={form.contact_nom} onChange={(e) => set("contact_nom")(e.target.value)} data-testid="lead-contact-nom" /></div>
          <div className="space-y-1.5"><Label className={L}>Email *</Label><Input type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} data-testid="lead-email" /></div>
          <div className="space-y-1.5"><Label className={L}>Téléphone</Label><Input value={form.telephone} onChange={(e) => set("telephone")(e.target.value)} data-testid="lead-telephone" /></div>
          <div className="space-y-1.5"><Label className={L}>Secteur</Label>
            <select value={form.secteur} onChange={(e) => set("secteur")(e.target.value)} data-testid="lead-secteur" className="h-10 w-full border border-[#DEE2E6] bg-white px-2 text-sm">
              <option value="">—</option>{secteurs.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="space-y-1.5"><Label className={L}>Ville</Label><Input value={form.ville} onChange={(e) => set("ville")(e.target.value)} data-testid="lead-ville" /></div>
          <div className="md:col-span-2"><SiretInput label="SIRET du prospect (facultatif, recommandé)" value={form.siret} onChange={set("siret")} testid="lead-siret" hint="Le SIRET renforce la détection des doublons" /></div>
          <div className="md:col-span-2 space-y-1.5"><Label className={L}>Notes</Label><Textarea value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={3} placeholder="Contexte, besoin exprimé, prochaine étape…" data-testid="lead-notes" /></div>
        </div>
        {dup?.blocking?.length > 0 && (
          <div className="border-2 border-[#E60000] bg-[#FEF2F2] p-3 text-xs space-y-1" data-testid="lead-blocking">
            {dup.blocking.map((b) => <div key={b} className="flex items-start gap-2 text-[#7F1D1D]"><ShieldAlert className="h-4 w-4 shrink-0" />{b}</div>)}
          </div>
        )}
        {dup?.warnings?.length > 0 && !dup?.blocking?.length && (
          <div className="border-2 border-[#FFD700] bg-[#FFFBEB] p-3 text-xs space-y-1" data-testid="lead-warnings">
            {dup.warnings.map((w) => <div key={w} className="flex items-start gap-2 text-[#92400E]"><AlertTriangle className="h-4 w-4 shrink-0" />{w}</div>)}
            <div className="pt-1 text-[#92400E]">Si ce n'est pas le même établissement, confirmez l'enregistrement.</div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          {dup?.warnings?.length > 0 && !dup?.blocking?.length ? (
            <Button onClick={() => submit(true)} disabled={busy} className="bg-[#F59E0B] text-white" data-testid="lead-confirm-warning">Confirmer malgré l'avertissement</Button>
          ) : (
            <Button onClick={() => submit(false)} disabled={busy || !form.company_name.trim() || !form.contact_nom.trim() || !form.email.trim() || !!dup?.blocking?.length} className="bg-[#002FA7] text-white" data-testid="lead-save">{busy ? "Vérification…" : "Enregistrer le lead"}</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
